import React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import { AppProps } from 'next/app'
import * as Sentry from '@sentry/react'
import {
  Provider as SiteComponentsProvider,
  SearchContextProvider,
  HeaderContextProvider,
  ActiveStoryContextProvider,
  TrackingCookieProvider,
} from '@izettle/public-site-components/dist/help-center'
import '@izettle/public-site-components/dist/global.css'
import '@izettle/public-site-components/dist/help-center.css'

import { storyblokInstance } from '@app/services/storyblok-service'
import { FavIcon } from '@app/components/favicon'
import { Seo } from '@app/components/seo'
import { Tracking } from '@app/components/tracking'
import { localeToCountryCode, localeToLangCode } from '../utils/i18n'

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    enabled: process.env.NODE_ENV === 'production',
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  })
}

function Fallback() {
  return <h1>Something unexpected occurred</h1>
}

function MyApp({ Component, pageProps }: AppProps) {
  const { locale = '', linkMap, page } = pageProps
  const countryCode = localeToCountryCode(locale)
  const langCode = localeToLangCode(locale)

  let seoProps: any = null
  try {
    const { content, uuid } = page?.data?.story
    seoProps = {
      ...content.seo,
      countryCode,
      availableCountries: content.availableCountries,
      urls: linkMap[uuid],
    }
  } catch {}

  return (
    <Sentry.ErrorBoundary fallback={Fallback}>
      <FavIcon />
      {seoProps && <Seo {...seoProps} />}
      <Tracking
        countryCode={countryCode}
        langCode={langCode}
        name={page?.data?.story?.name}
      />

      <Head>
        <script
          dangerouslySetInnerHTML={{
            __html: `var StoryblokCacheVersion = '${storyblokInstance.getCacheVersion()}';`,
          }}
        ></script>
      </Head>
      <SiteComponentsProvider
        provider="next"
        components={{ Link }}
        internalLinks={linkMap}
        lang={countryCode}
        storyUuid={page?.data?.story?.uuid}
      >
        <SearchContextProvider>
          <HeaderContextProvider>
            <ActiveStoryContextProvider>
              <TrackingCookieProvider>
                <Component {...pageProps} />
              </TrackingCookieProvider>
            </ActiveStoryContextProvider>
          </HeaderContextProvider>
        </SearchContextProvider>
      </SiteComponentsProvider>
    </Sentry.ErrorBoundary>
  )
}

export default MyApp
